import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { productApi } from '../../api';

/**
 * Debounced student lookup for forms that name a single student — TC issue,
 * bonafide, gate pass, concession. Stores the admission number (rule D-08:
 * human-readable codes in URLs and payloads, not UUIDs).
 *
 * Search matches name or admission number server-side; the widget itself
 * only debounces typing and caches per institution + query.
 */
interface Props {
  institution: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  status?: 'error' | 'warning';
  id?: string;
  autoFocus?: boolean;
}

export function StudentPickerField({ institution, value, onChange, placeholder, ...rest }: Props) {
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['student-picker', institution, q],
    queryFn: () => productApi.listStudents(institution, { q: q || undefined }),
    enabled: !!institution,
    staleTime: 30_000,
  });

  const options = useMemo(() => {
    const rows = list.data?.rows ?? [];
    return rows.map((r) => ({
      value: r.admission_number,
      label: `${r.name} · ${r.grade}${r.section ? '-' + r.section : ''} · ${r.admission_number}`,
    }));
  }, [list.data]);

  return (
    <Select
      showSearch
      value={value || undefined}
      onChange={(v) => onChange?.(v)}
      onSearch={setQ}
      filterOption={false}
      options={options}
      loading={list.isFetching}
      placeholder={placeholder ?? 'Search by name or admission no.'}
      notFoundContent={list.isFetching ? 'Searching…' : q ? 'No match' : 'Start typing'}
      allowClear
      {...rest}
    />
  );
}
